// Pure time helpers shared by the time picker family controllers
// (ui--time-picker, ui--time-picker-scrollable, ui--time-range-picker).
// Ported 1:1 from @a-safe-digital/react-time-picker so the Hotwire version
// behaves identically. The Ruby mirror lives in ui/time_value_parsing.rb.

export function pad(n) {
  return String(n).padStart(2, "0")
}

// Hour column values: 0..23 (24h) or 1..12 (12h).
export function hourValues(hourFormat) {
  if (hourFormat === 12) return Array.from({ length: 12 }, (_, i) => i + 1)
  return Array.from({ length: 24 }, (_, i) => i)
}

// Minute/second column values stepped by interval (1,5,10,15,30).
export function stepValues(interval) {
  const step = interval > 0 ? interval : 1
  const out = []
  for (let i = 0; i < 60; i += step) out.push(i)
  return out
}

// The 1-12 hour shown in 12h mode for a real 0-23 hour (0/12 -> 12).
export function displayHour(hour24, hourFormat) {
  if (hourFormat !== 12) return hour24
  const h = hour24 % 12
  return h === 0 ? 12 : h
}

// "AM" / "PM" for a real 0-23 hour.
export function meridiem(hour24) {
  return hour24 >= 12 ? "PM" : "AM"
}

// Compose the real 0-23 hour from a displayed 1-12 hour + explicit meridiem.
// (The AM/PM toggle makes this explicit — simpler and more correct than the
// React original, which infers AM/PM from the current value.)
export function to24Hour(displayedHour, meridiemVal) {
  const h = displayedHour % 12 // 12 -> 0
  return meridiemVal === "PM" ? h + 12 : h
}

// Localized display string, mirroring
// toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit", [second], hour12 }).
export function formatDisplay(hour, minute, second, hourFormat, showSeconds) {
  const date = new Date(2000, 0, 1, hour, minute, second)
  const opts = { hour: "2-digit", minute: "2-digit", hour12: hourFormat === 12 }
  if (showSeconds) opts.second = "2-digit"
  return date.toLocaleTimeString("en-US", opts)
}

// Canonical 24h value for the hidden form input.
export function canonicalValue(hour, minute, second, showSeconds) {
  const base = `${pad(hour)}:${pad(minute)}`
  return showSeconds ? `${base}:${pad(second)}` : base
}

// 12h hour label with AM/PM baked in (used by the scrollable/range variant):
// "01 AM", "12 PM", … Handles the full 0..23 range.
export function formatHourLabel(hour24, hourFormat) {
  if (hourFormat !== 12) return pad(hour24)
  return `${pad(displayHour(hour24, 12))} ${meridiem(hour24)}`
}

// Seconds-from-midnight, inclusive at the bounds — mirrors React isTimeDisabled.
export function isTimeDisabled(hour, minute, second, minTime, maxTime) {
  if (!minTime && !maxTime) return false
  const value = hour * 3600 + minute * 60 + second
  if (minTime) {
    const min =
      minTime.hour * 3600 + minTime.minute * 60 + (minTime.second || 0)
    if (value < min) return true
  }
  if (maxTime) {
    const max =
      maxTime.hour * 3600 + maxTime.minute * 60 + (maxTime.second || 0)
    if (value > max) return true
  }
  return false
}

// Parse a "HH:MM[:SS]" canonical string into { hour, minute, second } or null.
export function parseCanonical(str) {
  if (!str) return null
  const m = String(str).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/)
  if (!m) return null
  return { hour: +m[1], minute: +m[2], second: m[3] ? +m[3] : 0 }
}
